import React, { FunctionComponent } from 'react'
import { HostData } from '../Customer/Data'
import { BriefDonut } from './BriefDonut'
import { HighlightsSectionIcon, OfficePrinterIcon } from '../Icons/Icons'
import { BriefLoading } from './BriefLoading'
import { BriefShell, BriefContentContext } from './BriefShell'
import { DevicesSection, PrintQueuesSection } from './DeviceSections'
import { CompositionValue, CountValue, DevicesData, Note, PrintQueuesData } from './BriefSectionTypes'
import { findNote, formatThousands } from './briefHelpers'
import { SectionWithNote } from './SectionWithNote'
import { BriefPreview } from './BriefPreview'
import { KeyInsights } from './KeyInsights'
import { useBriefData } from './useBriefData'
import { useCustomerProfile } from './useCustomerProfile'
import styles from './BriefShell.module.css'
import healthStyles from './HealthCheckBrief.module.css'

interface Props {
  teamID: string
  host: HostData
}

export type HighlightMetric =
  | { id: string; kind: 'count'; count: CountValue }
  | { id: string; kind: 'composition'; composition: CompositionValue }

export interface FleetMakeupSection {
  id: string
  title?: string
  metrics?: HighlightMetric[]
  devices?: DevicesData
  printQueues?: PrintQueuesData
  notes?: Note[]
}

export interface FleetMakeupBriefing {
  type: string
  data: { sections: FleetMakeupSection[] }
  insights?: string[]
  createdAt: string
}

const EMPTY_VALUE = '—'
const NO_DATA_SUBTEXT = 'No data available'

export const HIGHLIGHT_LABELS: Record<string, string> = {
  'total-device-count': 'Total device count',
  'printer-count': 'Printer count',
  'eol-devices': 'End-of-life devices',
  'device-makeup-by-brand': 'Device makeup by brand',
  'embedded-vs-non-embedded': 'Embedded vs non-embedded',
}

/* line icons in the Health check's style: 24px, 1.5 stroke, round ends */
const LineIcon: FunctionComponent = ({ children }) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    {children}
  </svg>
)

const WarningLineIcon: FunctionComponent = () => (
  <LineIcon>
    <path d='M10.7 3.2 1.95 18.4a1.5 1.5 0 0 0 1.3 2.25h17.5a1.5 1.5 0 0 0 1.3-2.25L13.3 3.2a1.5 1.5 0 0 0-2.6 0Z' />
    <path d='M12 9v4.5' />
    <path d='M12 17.25h0.01' />
  </LineIcon>
)

export const HIGHLIGHT_ICONS: Record<string, FunctionComponent> = {
  'total-device-count': OfficePrinterIcon,
  'printer-count': OfficePrinterIcon,
  'eol-devices': WarningLineIcon,
}

const highlightLabel = (id: string): string => HIGHLIGHT_LABELS[id] ?? id

const FleetTile: FunctionComponent<{ tile: { key: string; label: string; value: string } }> = ({ tile }) => (
  <div className={`surface ${styles.metricSurface}`}>
    <div className={styles.tileValue} data-value-size='sm'>
      {tile.value}
    </div>
    <h5>{tile.label}</h5>
  </div>
)

const HighlightCountTile: FunctionComponent<{ metric: Extract<HighlightMetric, { kind: 'count' }> }> = ({ metric }) => {
  const noData = !metric.count.hasData
  const Icon = HIGHLIGHT_ICONS[metric.id]
  return (
    <div className={`surface ${styles.metricSurface}`}>
      <div className='flex flex-nowrap'>
        {Icon && (
          <i className='icon thirtytwo flex-noshrink push'>
            <Icon />
          </i>
        )}
        <div>
          <div className={styles.tileValue} data-value-size='md'>
            {noData ? EMPTY_VALUE : formatThousands(metric.count.value)}
            {noData && <span className={styles.comingSoon}>{NO_DATA_SUBTEXT}</span>}
          </div>
          <h5>{highlightLabel(metric.id)}</h5>
        </div>
      </div>
    </div>
  )
}

const HighlightChart: FunctionComponent<{ metric: Extract<HighlightMetric, { kind: 'composition' }> }> = ({
  metric,
}) => (
  <div className={healthStyles.makeupTile}>
    <div className={healthStyles.chartContainer}>
      <BriefDonut
        id={`${metric.id}-donut`}
        title={formatThousands(metric.composition.total)}
        data={metric.composition.segments.map(segment => [segment.label, segment.count] as [string, number])}
      />
    </div>
    <header className={healthStyles.makeupHeader}>
      <h5>{highlightLabel(metric.id)}</h5>
    </header>
  </div>
)

const HighlightsSection: FunctionComponent<{ title?: string; metrics: HighlightMetric[] }> = ({ title, metrics }) => {
  const counts = metrics.filter(
    (metric): metric is Extract<HighlightMetric, { kind: 'count' }> => metric.kind === 'count'
  )
  const charts = metrics.filter(
    (metric): metric is Extract<HighlightMetric, { kind: 'composition' }> => metric.kind === 'composition'
  )
  if (counts.length === 0 && charts.length === 0) {
    return null
  }

  return (
    <div className={styles.sectionWrapper} data-testid='highlights-section'>
      <header className={styles.sectionHeader}>
        <figure className='angular'>
          <i className='icon thirtytwo flex-noshrink'>
            <HighlightsSectionIcon />
          </i>
        </figure>
        <h4 className={styles.sectionTitle}>{title || 'Highlights'}</h4>
      </header>
      {counts.length > 0 && (
        <div className={`${styles.metricGrid} ${styles.threeColumnMetricGrid}`} data-testid='highlights-tiles'>
          {counts.map(metric => (
            <HighlightCountTile key={metric.id} metric={metric} />
          ))}
        </div>
      )}
      {charts.length > 0 && (
        <div className={`${healthStyles.makeupGrid} ${styles.sectionContainer}`} data-testid='highlights-charts'>
          {charts.map(metric => (
            <HighlightChart key={metric.id} metric={metric} />
          ))}
        </div>
      )}
    </div>
  )
}

export const FleetMakeupBrief: FunctionComponent<Props> = ({ teamID, host }) => {
  const { briefing, loading, error } = useBriefData<FleetMakeupBriefing>({
    teamID,
    customerID: host.id,
    briefType: 'fleet-makeup',
    errorFallback: 'An error occurred while generating the Fleet makeup brief',
  })
  const { profile, loading: profileLoading } = useCustomerProfile(teamID, host.id)

  if (loading || profileLoading) {
    return (
      <div className={`empty loading ${styles.loadingState}`} data-testid='loading-dots'>
        <div className={styles.loadingMessage}>
          <BriefLoading />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`alert error ${styles.errorState}`} data-testid='fleet-makeup-error'>
        <div>Failed to generate Fleet makeup brief: {error}</div>
      </div>
    )
  }

  const sections = briefing?.data?.sections ?? []
  const highlights = sections.find(section => section.id === 'highlights')
  const highlightMetrics = highlights?.metrics ?? []
  const hasHighlights = highlightMetrics.some(metric => metric.kind === 'count' || metric.kind === 'composition')
  const devices = sections.find(section => section.id === 'devices')?.devices?.devices ?? []
  const queues = sections.find(section => section.id === 'print-queues')?.printQueues?.queues ?? []
  const hasDevices = devices.length > 0
  const hasQueues = queues.length > 0

  const renderContent = ({ activeSideNotes, onNoteLayoutChange }: BriefContentContext): React.ReactNode => {
    if (!hasHighlights && !hasDevices && !hasQueues) {
      // nothing to show yet: the brief's shape, blurred, under one empty-state message
      return <BriefPreview testId='fleet-makeup-empty-state' />
    }

    return (
      <>
        {hasHighlights && <HighlightsSection title={highlights?.title} metrics={highlightMetrics} />}
        {hasDevices && (
          <SectionWithNote
            activeSideNotes={activeSideNotes}
            noteTitle='Devices with embedded apps'
            note={findNote(sections, 'devices', 'devices')}
            segmentTestId='devices-segment'
            noteTestId='devices-note'
            onNoteLayoutChange={onNoteLayoutChange}
            defaultExpanded // the brief's first note opens expanded
          >
            <DevicesSection
              devices={devices}
              renderTile={tile => <FleetTile tile={tile} />}
              renderSectionIcon={() => <OfficePrinterIcon />}
            />
          </SectionWithNote>
        )}
        {hasQueues && (
          <SectionWithNote
            activeSideNotes={activeSideNotes}
            noteTitle='Print queues'
            note={findNote(sections, 'print-queues', 'print-queues')}
            segmentTestId='print-queues-segment'
            noteTestId='print-queues-note'
            onNoteLayoutChange={onNoteLayoutChange}
            defaultExpanded={!hasDevices} // first when there are no devices
          >
            <PrintQueuesSection
              queues={queues}
              renderTile={tile => <FleetTile tile={tile} />}
              renderSectionIcon={() => <OfficePrinterIcon />}
            />
          </SectionWithNote>
        )}
      </>
    )
  }

  return (
    <BriefShell
      teamID={teamID}
      host={host}
      briefType='fleet-makeup'
      title='Fleet makeup'
      renderOverview={() => <KeyInsights insights={briefing?.insights ?? []} createdAt={briefing?.createdAt} />}
      generatedAt={briefing?.createdAt}
      industry={profile?.industry}
      layoutContent={briefing}
      suggestionChips={[
        'Make the case for a refresh',
        'Draft an email to the customer',
        'Talking points for my meeting',
      ]}
    >
      {renderContent}
    </BriefShell>
  )
}
